import React, { useEffect, useMemo } from 'react';
import {
  Card,
  Button,
  Form,
  Typography,
  Select,
  DatePicker,
  Row,
  Col,
  Divider,
  Input,
  Space,
  Spin,
  Alert,
  InputNumber,
} from 'antd';
import { ArrowLeftOutlined, SaveOutlined, MinusCircleOutlined, PlusOutlined } from '@ant-design/icons';
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import dayjs from 'dayjs';
import { ProjectSelector } from '@/components/project/ProjectSelector';
import { DepartmentSelect } from '@/components/finance/DepartmentSelect';
import { useCreateCAPA, useCAPA, useUpdateCAPA, useNCR } from '../../hooks/useQuality';
import { useQuery } from '@tanstack/react-query';
import employeeService, { employeeKeys } from '@/services/employee.service';

const { Title, Text } = Typography;
const { TextArea } = Input;

type CAPAFormPageProps = {
  mode?: 'create' | 'edit';
};

const CAPAFormPage: React.FC<CAPAFormPageProps> = ({ mode: modeProp }) => {
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const mode = modeProp || (id ? 'edit' : 'create');

  const ncrIdFromQuery = searchParams.get('ncr_id') || undefined;
  const prefills = (location.state || {}) as Record<string, unknown>;
  const linkedNcrId = (prefills.ncr_id as string) || ncrIdFromQuery;

  const { data: capa, isLoading: capaLoading } = useCAPA(mode === 'edit' ? id || '' : '');
  const { data: linkedNcr } = useNCR(linkedNcrId || '');
  const createMutation = useCreateCAPA();
  const updateMutation = useUpdateCAPA();

  const { data: employees = [] } = useQuery({
    queryKey: employeeKeys.list(),
    queryFn: () => employeeService.list(),
    staleTime: 60_000,
  });

  const employeeOptions = useMemo(
    () =>
      (employees as any[]).map((e) => ({
        value: e.id,
        label:
          `${e.employee_number || ''} ${e.first_name || ''} ${e.last_name || ''}`.trim() || e.id,
      })),
    [employees]
  );

  useEffect(() => {
    if (mode === 'edit' && capa) {
      form.setFieldsValue({
        source: capa.source,
        type: capa.type,
        source_reference: capa.source_reference,
        ncr_id: capa.ncr_id,
        project_id: capa.project_id,
        department_id: capa.department_id || undefined,
        description: capa.description,
        problem_statement: capa.problem_statement,
        action_plan: capa.action_plan,
        action_steps: capa.action_steps || [],
        responsible_person_id: capa.responsible_person_id,
        target_completion_date: capa.target_completion_date
          ? dayjs(capa.target_completion_date)
          : undefined,
        extended_date: capa.extended_date ? dayjs(capa.extended_date) : undefined,
        extension_reason: capa.extension_reason,
        progress_percentage: capa.progress_percentage ?? 0,
        progress_notes: capa.progress_notes,
        effectiveness_criteria: capa.effectiveness_criteria,
      });
      return;
    }

    if (mode === 'create') {
      form.setFieldsValue({
        source: (prefills.source as string) || (linkedNcrId ? 'NCR' : 'Other'),
        type: (prefills.type as string) || 'Corrective Action',
        source_reference:
          (prefills.source_reference as string) || linkedNcr?.ncr_number || undefined,
        ncr_id: linkedNcrId,
        project_id: (prefills.project_id as string) || linkedNcr?.project_id || undefined,
        department_id:
          (prefills.department_id as string) || linkedNcr?.department_id || undefined,
        description: (prefills.description as string) || undefined,
        problem_statement: (prefills.problem_statement as string) || undefined,
        action_plan: (prefills.action_plan as string) || undefined,
        target_completion_date: dayjs().add(30, 'day'),
        progress_percentage: 0,
        action_steps: [],
      });
    }
  }, [mode, capa, linkedNcrId, linkedNcr, form]);

  const handleSubmit = async (values: Record<string, any>) => {
    const payload = {
      source: values.source,
      type: values.type,
      source_reference: values.source_reference || undefined,
      ncr_id: values.ncr_id || undefined,
      project_id: values.project_id || undefined,
      department_id: values.department_id || null,
      description: values.description,
      problem_statement: values.problem_statement || undefined,
      action_plan: values.action_plan,
      action_steps: values.action_steps || [],
      responsible_person_id: values.responsible_person_id,
      target_completion_date: values.target_completion_date
        ? values.target_completion_date.format('YYYY-MM-DD')
        : undefined,
      extended_date: values.extended_date
        ? values.extended_date.format('YYYY-MM-DD')
        : undefined,
      extension_reason: values.extension_reason || undefined,
      progress_percentage: values.progress_percentage ?? 0,
      progress_notes: values.progress_notes || undefined,
      effectiveness_criteria: values.effectiveness_criteria || undefined,
    };

    try {
      if (mode === 'edit' && id) {
        await updateMutation.mutateAsync({ id, data: payload });
        navigate(`/qms/capas/${id}`);
      } else {
        const created = await createMutation.mutateAsync(payload);
        navigate(`/qms/capas/${created.id}`);
      }
    } catch {
      /* toast from hook */
    }
  };

  if (mode === 'edit' && capaLoading) {
    return (
      <div style={{ padding: 48, textAlign: 'center' }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div style={{ padding: 24, maxWidth: 960, margin: '0 auto' }}>
      <Space style={{ marginBottom: 24 }}>
        <Button
          icon={<ArrowLeftOutlined />}
          onClick={() => navigate(mode === 'edit' && id ? `/qms/capas/${id}` : '/qms/capas')}
        >
          Back
        </Button>
        <Title level={2} style={{ margin: 0 }}>
          {mode === 'edit' ? `Edit CAPA ${capa?.capa_number || ''}` : 'Create CAPA'}
        </Title>
      </Space>

      {(linkedNcrId || linkedNcr) && (
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
          message="Linked from NCR"
          description={
            linkedNcr
              ? `${linkedNcr.ncr_number} — ${linkedNcr.description?.slice(0, 160) || ''}`
              : `NCR id ${linkedNcrId}`
          }
          action={
            linkedNcrId ? (
              <Button size="small" onClick={() => navigate(`/qms/ncrs/${linkedNcrId}`)}>
                View NCR
              </Button>
            ) : undefined
          }
        />
      )}

      <Card>
        <Form form={form} layout="vertical" onFinish={handleSubmit}>
          <Divider orientation="left">Source & link</Divider>
          <Row gutter={16}>
            <Col span={8}>
              <Form.Item name="source" label="Source" rules={[{ required: true }]}>
                <Select>
                  <Select.Option value="NCR">NCR</Select.Option>
                  <Select.Option value="Internal Audit">Internal Audit</Select.Option>
                  <Select.Option value="Management Review">Management Review</Select.Option>
                  <Select.Option value="Preventive Action">Preventive Action</Select.Option>
                  <Select.Option value="Customer Feedback">Customer Feedback</Select.Option>
                  <Select.Option value="Other">Other</Select.Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="type" label="Type" rules={[{ required: true }]}>
                <Select>
                  <Select.Option value="Corrective Action">Corrective Action</Select.Option>
                  <Select.Option value="Preventive Action">Preventive Action</Select.Option>
                  <Select.Option value="Both">Both</Select.Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="source_reference" label="Source reference">
                <Input placeholder="e.g. NCR number" />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item name="ncr_id" hidden>
            <Input />
          </Form.Item>
          <Form.Item name="project_id" label="Project">
            <ProjectSelector
              allowClear
              placeholder="Optional project"
              onChange={(projectId) => form.setFieldValue('project_id', projectId)}
            />
          </Form.Item>
          <Form.Item name="department_id" label="Department">
            <DepartmentSelect allowClear placeholder="Dept owning this CAPA" />
          </Form.Item>

          <Divider orientation="left">Problem</Divider>
          <Form.Item
            name="description"
            label="Description"
            rules={[{ required: true, message: 'Description is required' }]}
          >
            <TextArea rows={3} placeholder="What systemic issue is being addressed?" />
          </Form.Item>
          <Form.Item name="problem_statement" label="Problem statement">
            <TextArea rows={4} placeholder="Context from NCR / audit / feedback" />
          </Form.Item>

          <Divider orientation="left">Action plan / steps</Divider>
          <Form.Item
            name="action_plan"
            label="Action plan"
            rules={[{ required: true, message: 'Action plan is required' }]}
          >
            <TextArea rows={4} placeholder="Overall corrective / preventive plan" />
          </Form.Item>
          <Form.List name="action_steps">
            {(fields, { add, remove }) => (
              <>
                {fields.map((field) => (
                  <Row gutter={8} key={field.key} align="middle" style={{ marginBottom: 8 }}>
                    <Col flex="auto">
                      <Form.Item
                        {...field}
                        name={[field.name, 'step']}
                        rules={[{ required: true, message: 'Step required' }]}
                        style={{ marginBottom: 0 }}
                      >
                        <Input placeholder="Action step" />
                      </Form.Item>
                    </Col>
                    <Col>
                      <MinusCircleOutlined onClick={() => remove(field.name)} />
                    </Col>
                  </Row>
                ))}
                <Button type="dashed" onClick={() => add({ step: '', done: false })} block icon={<PlusOutlined />}>
                  Add action step
                </Button>
              </>
            )}
          </Form.List>

          <Divider orientation="left">Ownership & dates</Divider>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="responsible_person_id"
                label="Responsible person"
                rules={[{ required: true, message: 'Responsible person is required' }]}
              >
                <Select
                  showSearch
                  optionFilterProp="label"
                  options={employeeOptions}
                  placeholder="Select employee"
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="target_completion_date"
                label="Target completion"
                rules={[{ required: true, message: 'Target date is required' }]}
              >
                <DatePicker style={{ width: '100%' }} format="DD/MM/YYYY" />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={8}>
              <Form.Item name="progress_percentage" label="Progress %">
                <InputNumber min={0} max={100} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={16}>
              <Form.Item name="progress_notes" label="Progress notes">
                <Input placeholder="Optional notes" />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="extended_date" label="Extended date">
                <DatePicker style={{ width: '100%' }} format="DD/MM/YYYY" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="extension_reason" label="Extension / late reason">
                <Input />
              </Form.Item>
            </Col>
          </Row>

          <Divider orientation="left">Effectiveness</Divider>
          <Form.Item
            name="effectiveness_criteria"
            label="Effectiveness criteria"
            extra="How will you know the CAPA worked? Verified on the CAPA detail page."
          >
            <TextArea rows={3} />
          </Form.Item>

          <Space style={{ marginTop: 16 }}>
            <Button
              type="primary"
              htmlType="submit"
              icon={<SaveOutlined />}
              loading={createMutation.isPending || updateMutation.isPending}
            >
              {mode === 'edit' ? 'Save changes' : 'Create CAPA'}
            </Button>
            <Text type="secondary">Numbering uses Settings document sequence (CAPA).</Text>
          </Space>
        </Form>
      </Card>
    </div>
  );
};

export default CAPAFormPage;
